// 3rd party
import { computed, toJS, action, observable } from 'mobx';
import get from 'lodash/get';
import isString from 'lodash/isString';
import * as isWithinRange from 'date-fns/is_within_range';
import * as isBefore from 'date-fns/is_before';
import * as isAfter from 'date-fns/is_after';
// external dependencies
import { IQueryContext } from '@kurtosys/ksys-app-template/dist/models/query';
import { isArrayLike } from '@kurtosys/ksys-app-template/dist/utils/isArrayLike';
import {
    isNullOrUndefined,
    isNullOrEmpty,
} from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
// stores
import { BaseStore, DataStore } from '.';
// components
import { translate } from '../Components/Translate';
// models
import { Stores } from '../models/Stores';
import {
    AxisTickOptions,
    Chart,
    DateOptions,
} from '../models/configuration/chart/Chart';
import { ShareClass } from '../models/entity';
import { DataLocator } from '../models/configuration/DataLocator';
import { ChartConfig } from '../models/configuration/chart/ChartConfig';
import { XAxis } from '../models/configuration/chart/XAxis';
import { YAxis } from '../models/configuration/chart/YAxis';
import { Series } from '../models/configuration/chart/Series';
import {
    PeriodSelector,
    PeriodSelectorOptions,
} from '../models/configuration/chart/PeriodSelector';
import { AsAtDate } from '../models/configuration/AsAtDate';
import { DateSelector } from '../models/configuration/chart/DateSelector';
import {
    AsAtDateValue,
    DataSetValue,
    PeriodSelectorExtendedOptions,
    PeriodSelectorOptionWithDates,
} from '../models/components/ChartStore';
// utils
import getDataPoint from '../utils/getDataPoint';
import getQueryResults from '../utils/query/getQueryResults';
import getSelectedChartPeriod from '../utils/getSelectedChartPeriod';
import performanceCheck from '../utils/performanceCheck';
import { getDisclaimers } from '../utils/disclaimers';
import { Axis, Domain } from '../models/configuration/chart/Axis';
import { DatePeriod } from '@kurtosys/ksys-app-template/dist/models/common';
import { CsvHelper } from '../utils/CsvHelper';
import { IconOptions } from '../models/configuration/explorer/DownloadControls';
import { RebaseHelper } from '@kurtosys/ksys-app-template/dist/helpers';
import { IRebaseOptions } from '@kurtosys/ksys-app-template/dist/models/data';
import formatter from '../utils/formatter';
import { default as addDate } from '../utils/date/add';
import React from 'react';
import Autocomplete from '../Components/Autocomplete';

export type TDataKey =
    | string
    | number
    | ((dataObject: any) => string | number | [number, number] | null);

export type TAxisDomain =
    | string
    | number
    | TContentRenderer<any>
    | 'auto'
    | 'dataMin'
    | 'dataMax';

export type TContentRenderer<P> = (props: P) => any;

export class ChartStore extends BaseStore {
    rebaser: RebaseHelper;

    startAutoCompleteRef = React.createRef<typeof Autocomplete>();
    endAutoCompleteRef = React.createRef<typeof Autocomplete>();

    @observable startDate: string;
    @observable endDate: string;
    // Used to hide the selected period when using the date selectors
    @observable showSelectedPeriod: boolean;
    @observable.ref periodSelector: PeriodSelectorExtendedOptions;
    @observable.ref dataSet: Array<DataSetValue>;

    constructor(
        stores: Stores,
        public chartConfig: Chart,
        // TODO: pull in entityStore instead
        public queryContext: IQueryContext,
        public shareClass: ShareClass
    ) {
        super(stores);
        this.dataSet = this.getDataSet();
        const {
            periodSelector,
            startDate,
            endDate,
        } = this.initPeriodSelector();
        this.periodSelector = periodSelector;
        this.startDate = startDate;
        this.endDate = endDate;
        this.rebaser = new RebaseHelper(this.rebaseConfig);
    }

    get dataStore(): DataStore {
        const { data } = this.stores;
        if (data) {
            return data;
        }
    }

    get rebaseConfig(): IRebaseOptions {
        if (this.dataStore && this.config) {
            const { rebase, rebaseQueries } = this.config;
            if (rebase || rebaseQueries) {
                const { query } = this.dataStore.stores;
                if (query) {
                    const executionOptions = query.getExecutionOptionsUsingContext(
                        this.queryContext
                    );
                    const rebaseConfig = query.mergeQueriesAndProps<
                        IRebaseOptions
                    >(
                        {
                            props: { ...rebase },
                            queries: { ...rebaseQueries },
                        },
                        executionOptions
                    );
                    return rebaseConfig;
                }
            }
        }
    }

    @computed
    get isMobile(): boolean {
        const { data: dataStore } = this.stores;
        return dataStore.isMobile;
    }

    @computed
    get locale(): string {
        const { data: dataStore } = this.stores;
        return dataStore.culture;
    }

    @computed
    get fundCurrency(): string {
        const { data: dataStore } = this.stores;
        return get(
            this.shareClass,
            `properties_pub.${dataStore.config.fundCurrencyProperty ||
            'ccy'}.value`
        );
    }

    // Chart data
    getDataSet() {
        const { data: dataStore, translation: translationStore } = this.stores;
        const {
            data,
            config: { dataMap = [], series },
            hideEmptyDataPoints = false,
            inceptionInjection,
        } = this.chartConfig;
        let dataSet = [];

        if (isArrayLike(data)) {
            dataSet = (data as []).map(d => {
                this.sortDataForChartControls(d);
                return getDataPoint({
                    dataStore,
                    dataConfig: d,
                    shareClass: this.shareClass,
                    locale: this.locale,
                    fundCurrency: this.fundCurrency,
                    defaultNoDataPlaceholder: 0,
                    hasPerformance: this.hasPerformance,
                });
            });
        } else {
            // checks and sorts dates when using chart controls
            this.sortDataForChartControls(data);
            dataSet = getDataPoint({
                dataStore,
                dataConfig: data as DataLocator,
                shareClass: this.shareClass,
                locale: this.locale,
                fundCurrency: this.fundCurrency,
                defaultNoDataPlaceholder: 0,
                hasPerformance: this.hasPerformance,
            });
        }

        // Convert possible string "number" values to float for recharts to handle correctly
        for (const seriesItem of series) {
            const dataKey = seriesItem.dataKey;
            // tslint:disable-next-line:prefer-for-of
            for (let index = 0; index < dataSet.length; index++) {
                const element = dataSet[index];
                if (!isNullOrUndefined(element[dataKey])) {
                    // we are dealing with mobx observables and need to deref the dataset
                    const dataSetItem = { ...element };
                    dataSetItem[dataKey] = parseFloat(element[dataKey]);
                    // reassign to dataSet to replace ref to mobx observable.
                    dataSet[index] = dataSetItem;
                }
            }
        }

        if (inceptionInjection) {
            const { property, dateKey = 'date' } = inceptionInjection;
            if (property) {
                const inceptionDate = getDataPoint({
                    dataStore,
                    dataConfig: inceptionInjection.property,
                    shareClass: this.shareClass,
                    locale: this.locale,
                    fundCurrency: this.fundCurrency,
                    defaultNoDataPlaceholder: 0,
                    hasPerformance: this.hasPerformance,
                });
                if (inceptionDate) {
                    const row = {
                        [dateKey]: inceptionDate,
                    };
                    for (const seriesItem of series) {
                        const dataKey = seriesItem.dataKey;
                        row[dataKey] = 0;
                    }
                    dataSet.unshift(row);
                }
            }
        }

        if (dataMap.length > 0) {
            dataSet = dataMap.map(map => {
                const mappedData = {};

                for (const property in map) {
                    if (map[property]) {
                        mappedData[property] = translate(
                            translationStore,
                            map[property],
                            dataSet
                        );
                    }
                }

                return mappedData;
            });
        }

        if (this.rawXAxis && this.rawXAxis.ticksQuery) {
            dataSet = this.getAxisTicks(this.rawXAxis, dataSet);
        }

        if (this.rawYAxis && this.rawYAxis.ticksQuery) {
            dataSet = this.getAxisTicks(this.rawYAxis, dataSet);
        }

        if (hideEmptyDataPoints && !isNullOrEmpty(dataSet)) {
            return dataSet.filter(point => this.dataPointHasData(point));
        }
        return dataSet;
    }

    // Filtered chart data
    @computed
    get data(): Array<DataSetValue> {
        const { dateOptions } = this.chartConfig;
        const { xAxis, yAxis } = this.config;
        let dataSet;

        if (this.hasPeriodSelection) {
            dataSet = this.dataSet.filter(data => {
                const currentDate = get(data, this.dateProperty);
                return isWithinRange(currentDate, this.startDate, this.endDate);
            });
        } else {
            dataSet = this.dataSet;
        }

        if (dateOptions) {
            if (xAxis && dateOptions.xAxisDateAsNumber) {
                dataSet = this.useDateAsNumber(xAxis, dataSet);
            }
            if (yAxis && dateOptions.yAxisDateAsNumber) {
                dataSet = this.useDateAsNumber(yAxis, dataSet);
            }
        }

        return dataSet;
    }

    // Date property in chart data
    @computed
    get dateProperty(): string {
        const { chartControls = {} } = this.chartConfig;
        const { dateProperty = 'date' } = chartControls;
        return dateProperty;
    }

    // Chart dates for date selectors
    @computed
    get chartDatesStart(): Array<string> {
        return this.dataSet
            .map(data => {
                const date = get(data, this.dateProperty);
                if (isBefore(date, this.endDate)) {
                    return date;
                }
            })
            .filter(d => d !== undefined);
    }

    // CSV Download
    @computed
    get showCsvDownload(): boolean {
        // only allow the csv download for LineCharts
        if (this.chartConfig.type !== 'LineChart') {
            return false;
        }
        return this.chartConfig.csvDownload?.show || false;
    }

    @computed
    get csvIconOptions(): IconOptions {
        const defaultIconOptions = {
            filename: 'DownloadIcon',
            hide: false,
        };

        if (this.chartConfig.csvDownload?.iconOptions) {
            return this.chartConfig.csvDownload.iconOptions;
        }
        return defaultIconOptions;
    }

    @computed
    get csvDownloadAlign(): 'left' | 'right' {
        return this.chartConfig.csvDownload?.align || 'right';
    }

    @action
    downloadCsv = () => {
        const { config, csvDownload } = this.chartConfig;
        const { columns, label, filename = 'Performance' } = csvDownload;
        if (this.showCsvDownload && columns) {
            const csv = [];
            const headings = [];

            let csvData = this.data.slice();
            if (config.rebase && config.rebaseQueries) {
                csvData = this.rebaser.rebase(this.data.slice());
            }

            let dataIndex = 0;
            for (const row of csvData) {
                const rowValues = [];

                for (const column of columns) {
                    let value = row[column.dataKey];
                    if (value === undefined) {
                        value = '-';
                    }

                    if (dataIndex === 0) {
                        headings.push(column.label);
                    }

                    if (column.format) {
                        value = formatter(
                            this.getBaseFormatOptions(column.format, value)
                        );
                    }

                    rowValues.push(value);
                }
                csv.push(rowValues);

                dataIndex++;
            }

            csv.unshift(headings);

            if (label && label.property) {
                csv.unshift(
                    this.shareClass.properties_pub[label.property]?.value || ''
                );
            }

            CsvHelper.escapeDisclaimers(this.performanceCheckDisclaimers)
                .split('\n')
                .map(disclaimer => csv.push([disclaimer]));
            this.stores.data.downloadCsv(csv.join('\n'), `${filename}.csv`);
        }
    };

    // Chart dates for date selectors
    @computed
    get chartDatesEnd(): Array<string> {
        return this.dataSet
            .map(data => {
                const date = get(data, this.dateProperty);
                if (isAfter(date, this.startDate)) {
                    return date;
                }
            })
            .filter(d => d !== undefined);
    }

    @computed
    get dateSelector(): DateSelector {
        const { chartControls = {} } = this.chartConfig;
        const { dateSelector } = chartControls;

        if (dateSelector) {
            const defaultDateSelector = {
                startDateLabel: 'Start date',
                endDateLabel: 'End date',
            };

            return {
                ...defaultDateSelector,
                ...dateSelector,
            };
        }

        return {};
    }

    @computed
    get hasDateSelector(): boolean {
        return Object.keys(this.dateSelector).length > 0;
    }

    @computed
    get hasPeriodSelector(): boolean {
        return this.periodSelector && this.periodSelector.options.length > 0;
    }

    @computed
    get hasPeriodSelection(): boolean {
        return this.hasDateSelector || this.hasPeriodSelector;
    }

    // Chart as at date
    @computed
    get asAtDate(): AsAtDateValue {
        const { data: dataStore } = this.stores;
        const { asAtDate = {} } = this.chartConfig;
        const { label, data, format } = asAtDate as AsAtDate;
        const value = getDataPoint({
            dataStore,
            dataConfig: { ...data, format },
            shareClass: this.shareClass,
            locale: this.locale,
            fundCurrency: this.fundCurrency,
            defaultNoDataPlaceholder: '-',
            hasPerformance: this.hasPerformance,
        });

        if (this.chartConfig.asAtDate) {
            return {
                label,
                value,
            };
        }

        return null;
    }

    // Chart display
    @computed
    get hasPerformance(): boolean {
        if (this.chartConfig.performanceCheck) {
            const { data: dataStore } = this.stores;
            const { shareClass } = this;
            const { config } = dataStore;
            return performanceCheck({
                dataStore,
                config,
                shareClass,
            });
        }

        // no performance check, carry on as normal
        return true;
    }

    @computed
    get displayChart(): boolean {
        const {
            performanceCheck: performanceCheckConfig,
            noDataHidden,
        } = this.chartConfig;
        if (!noDataHidden) {
            return true;
        }
        if (performanceCheckConfig) {
            return this.dataSet.length > 0 && this.hasPerformance;
        }

        return this.dataSet.length > 0;
    }

    // Chart disclaimers
    @computed
    get chartDisclaimers(): Array<string> {
        return this.disclaimerByType(this.chartConfig.disclaimerTypes);
    }

    @computed
    get noDataDisclaimers(): Array<string> {
        return this.disclaimerByType(this.chartConfig.noDataDisclaimerTypes);
    }

    @computed
    get performanceCheckDisclaimers(): Array<string> {
        return this.disclaimerByType(
            this.chartConfig.performanceCheckDisclaimerTypes
        );
    }

    @computed
    get displayDisclaimers(): Array<string> {
        if (this.chartHasData && !this.hasPerformance) {
            return this.performanceCheckDisclaimers;
        }

        return this.noDataDisclaimers;
    }

    // Chart config
    @computed
    get config(): ChartConfig {
        return this.chartConfig.config || {};
    }

    @computed
    get mobileConfig(): ChartConfig {
        return this.chartConfig.mobileConfig || {};
    }

    @computed
    get rawXAxis(): XAxis {
        const { xAxis } = this.config;

        if (this.isMobile) {
            const { xAxis: xAxisMobile } = this.mobileConfig;
            return toJS({ ...xAxis, ...xAxisMobile });
        }

        return toJS(xAxis);
    }

    @computed
    get xAxis(): XAxis {
        return this.applyCustomTickValues(this.rawXAxis, 'x');
    }

    @computed
    get rawYAxis(): YAxis {
        const { yAxis } = this.config;

        if (this.isMobile) {
            const { yAxis: yAxisMobile } = this.mobileConfig;
            return toJS({ ...yAxis, ...yAxisMobile });
        }

        return toJS(yAxis);
    }

    @computed
    get yAxis(): YAxis {
        return this.applyCustomTickValues(this.rawYAxis, 'y');
    }

    @computed
    get seriesList() {
        const { series = [] } = this.config;
        return toJS(series);
    }

    @computed
    get series(): Array<Series> {
        const { showBlankSeries = false } = this.config;
        const seriesList = this.seriesList;
        if (
            !showBlankSeries &&
            !isNullOrEmpty(seriesList) &&
            Array.isArray(seriesList)
        ) {
            return seriesList.filter(s => this.seriesHasData(s.dataKey));
        }
        return seriesList;
    }

    @computed
    get chartHasData(): boolean {
        const { seriesDataKeys = [] } = this;
        return (
            seriesDataKeys.filter(dataKey => this.seriesHasData(dataKey))
                .length > 0
        );
    }

    @computed
    get seriesDataKeys() {
        const { series, showBlankSeries = false } = this;
        if (
            !showBlankSeries &&
            !isNullOrEmpty(series) &&
            Array.isArray(series)
        ) {
            return series
                .filter(s => this.seriesHasData(s.dataKey))
                .map(s => s.dataKey);
        }
        return series.map(s => s.dataKey);
    }

    @computed
    get showBlankSeries(): boolean {
        const { showBlankSeries = false } = this.config;
        return showBlankSeries;
    }

    @computed
    get minHeight(): number {
        const { barChart, series } = this.config;
        const { isMobile } = this;
        if (isMobile) {
            let minHeight = 400;
            if (barChart) {
                const { barSize, barGap, barCategoryGap } = barChart;
                if (barSize && barGap && barCategoryGap) {
                    const dataCount = this.data.length;
                    const seriesCount = series.length;

                    const barSizes = dataCount * seriesCount * barSize;
                    const barGaps = dataCount * (seriesCount - 1) * barGap;
                    const categoryGaps = dataCount * barCategoryGap;

                    minHeight = barSizes + barGaps + categoryGaps;
                }
            }
            return minHeight;
        }
    }

    @computed
    get selectedChartPeriod() {
        const { periodSelector } = this;
        return getSelectedChartPeriod(periodSelector.options);
    }

    @computed
    get accessibleChartControlsLabel() {
        const { accessibility: accessibilityStore } = this.stores;
        const { chartConfig } = this;
        const {
            hasPeriodSelector,
            selectedChartPeriod,
            startDate,
            endDate,
            locale,
        } = this;
        return (
            (hasPeriodSelector && {
                'aria-label': `${accessibilityStore.chartProperty(
                    chartConfig.id,
                    'selectedChartPeriod',
                    {
                        selectedStartDate: accessibilityStore.formatDate(
                            startDate,
                            locale
                        ),
                        selectedEndDate: accessibilityStore.formatDate(
                            endDate,
                            locale
                        ),
                        selectedPeriod:
                            (selectedChartPeriod &&
                                selectedChartPeriod.label) ||
                            'custom',
                    }
                )}`,
            }) ||
            {}
        );
    }

    // Chart controls/date selections
    initPeriodSelector() {
        const { chartControls = {} } = this.chartConfig;
        const { periodSelector: periodSelectorConfig = {} } = chartControls;
        const {
            options: optionsConfig = [],
        } = periodSelectorConfig as PeriodSelector;
        this.showSelectedPeriod = true;
        const periodSelector = {
            ...periodSelectorConfig,
            options: optionsConfig.map(option => {
                const dateRange = this.getPeriodStartEndDates(option);
                return {
                    selected: false,
                    ...option,
                    ...dateRange,
                };
            }),
        };

        let period = this.getDefaultPeriod();
        if (this.hasPeriodSelection) {
            const { options = [] } = periodSelector;
            const selectedPeriod = options.find(o => o.selected);
            if (selectedPeriod) {
                period = this.getPeriodFromOption(selectedPeriod);
            }
        }

        return {
            periodSelector,
            ...period,
        };
    }

    getAxisTickCount(
        tickOptions: AxisTickOptions,
        selectedPeriod?: PeriodSelectorOptionWithDates
    ) {
        const {
            tickCount: defaultTickCount = 1,
            periodOptions = [],
        } = tickOptions;
        if (selectedPeriod) {
            const periodTickCount = periodOptions.filter(
                option =>
                    option.periodKey === selectedPeriod.period &&
                    option.periodValue === selectedPeriod.value
            );
            if (!isNullOrEmpty(periodTickCount)) {
                return periodTickCount[0].tickCount || defaultTickCount;
            }
        }
        return defaultTickCount;
    }

    useDateAsNumber(axis: any, data: Array<object>) {
        const response: Array<any> = [];
        if (data) {
            if (axis.dataKey) {
                axis.type = 'number';
                for (const element of data) {
                    const clone: any = { ...element };
                    let value = clone[axis.dataKey as string];
                    value = new Date(value).getTime();
                    clone[axis.dataKey as string] = value;
                    response.push(clone);
                }
            } else {
                response.push(...data);
            }
        }
        return response;
    }

    applyCustomTickValues(axis: Axis, type: 'x' | 'y') {
        const { dateOptions } = this.chartConfig;
        const tickOptions = this.getTickOptions(type, dateOptions);

        if (tickOptions) {
            let tickCount = this.getAxisTickCount(
                tickOptions,
                this.selectedChartPeriod
            );
            const datasetCount = this.data && this.data.length;
            if (datasetCount) {
                tickCount = Math.min(tickCount, datasetCount - 1);
            }

            if (tickCount > 0) {
                const data = this.data || [];
                const {
                    dataKey = 'date',
                    domain = ['dataMin', 'dataMax'],
                } = axis;
                const domainValues = this.getDomainValues(
                    domain,
                    dataKey,
                    data
                );
                axis.ticks = this.getCustomTicks(
                    domainValues,
                    tickCount,
                    dataKey,
                    data
                );
            }
        }
        return axis;
    }

    getTickOptions(
        elementType: string,
        dateOptions?: DateOptions
    ): AxisTickOptions | undefined {
        if (dateOptions) {
            if (elementType === 'x' && dateOptions.xAxisDateAsNumber) {
                return dateOptions.xAxisTickOptions;
            }
            if (elementType === 'y' && dateOptions.yAxisDateAsNumber) {
                return dateOptions.yAxisTickOptions;
            }
        }
    }

    getCustomTicks(
        domainValues: any,
        tickCount: number,
        dataKey: TDataKey = 'date',
        data: Array<object>
    ) {
        const { start, end } = domainValues;
        const interval = (end - start) / tickCount;

        const dataPoints = data.map(item =>
            this.getValueWithDataKey(item, dataKey)
        );

        const ticks: Array<number> = [start];

        for (let i = 1; i <= tickCount - 1; i++) {
            const target = start + interval * i;
            ticks.push(this.getClosestValue(target, dataPoints));
        }

        ticks.push(end);
        return ticks;
    }

    getClosestValue(target: number, values: Array<number>) {
        if (!isNullOrEmpty(values)) {
            const under = Math.max(...values.filter(value => value <= target));
            const over = Math.min(...values.filter(value => value >= target));

            if (target - under < over - target) {
                return under;
            }
            return over;
        }
        return target;
    }

    getDomainValues(
        domain: Array<Domain>,
        dataKey: TDataKey,
        data: Array<object>
    ) {
        let [start, end] = domain;
        if (start === 'dataMin') {
            start = this.getValueWithDataKey(data[0], dataKey);
        }
        if (end === 'dataMax') {
            end = this.getValueWithDataKey(data[data.length - 1], dataKey);
        }
        return { start, end };
    }

    getValueWithDataKey(dataObject: any, dataKey: TDataKey) {
        if (dataObject) {
            if (typeof dataKey === 'string' || typeof dataKey === 'number') {
                return dataObject[dataKey];
            }
            return dataKey(dataObject);
        }
    }

    // Chart controls needs to check that the current date is between the start and end dates.
    // Therefore to avoid date fns errors, we need to ensure that the start date is also before the end date.
    sortDataForChartControls(data: any) {
        const { chartControls } = this.chartConfig;
        if (chartControls) {
            data.sort = {
                type: 'date',
                key: this.dateProperty,
                direction: 'ASC',
            };
        }
    }

    getAxisTicks = (
        axis: XAxis | YAxis,
        data: Array<DataSetValue>
    ): Array<DataSetValue> => {
        const { queryContext } = this;
        if (data && isArrayLike(data) && axis.ticksQuery) {
            // mobx.toJS was not sufficiently preparing the data for the map to occur
            const results = data.slice().map((row, idx) => {
                // spread ticksQuery to deref from config store since we are mutating the object further down
                let query = { ...axis.ticksQuery };
                if (query.subQueries) {
                    // Amend the index for each subQuery
                    query.subQueries = query.subQueries.map(subQuery => {
                        return { ...subQuery, index: idx };
                    });
                } else {
                    // else amend for the query
                    query = { ...query, index: idx };
                }
                return {
                    ...row,
                    ...getQueryResults(
                        queryContext,
                        {
                            [axis.dataKey]: query,
                        },
                        this.locale
                    ),
                };
            });
            return results;
        }
    };

    dataPointHasData(dataPoint: any) {
        const seriesList = this.seriesList;
        return (
            seriesList.filter(item => !isNullOrEmpty(dataPoint[item.dataKey]))
                .length > 0
        );
    }

    getPeriodFromOption(periodOption: PeriodSelectorOptionWithDates) {
        return {
            startDate: periodOption.startDate,
            endDate: periodOption.endDate,
        };
    }

    getDefaultPeriod() {
        return {
            startDate: get(this.dataSet[0], this.dateProperty),
            endDate: get(
                this.dataSet[this.dataSet.length - 1],
                this.dateProperty
            ),
        };
    }

    @action
    setPeriod = (periodOption: PeriodSelectorOptionWithDates) => {
        const { data: dataStore } = this.stores;
        const { startDate, endDate } = this.getPeriodFromOption(periodOption);
        this.startDate = startDate;
        this.endDate = endDate;
        this.showSelectedPeriod = true;
        const options = this.periodSelector.options.map(opt => {
            return {
                ...opt,
                selected: periodOption.label === opt.label,
            };
        });
        this.periodSelector = { ...this.periodSelector, options };
        dataStore.logDataLayer('chart_period_selection', {
            chart: this.chartConfig.label,
            dateTarget: 'start',
            date: startDate,
            relatedEntityId: this.shareClass.clientCode,
        });
        dataStore.logDataLayer('chart_period_selection', {
            chart: this.chartConfig.label,
            dateTarget: 'end',
            date: endDate,
            relatedEntityId: this.shareClass.clientCode,
        });
    };

    @action
    setDefaultPeriod = () => {
        const { startDate, endDate } = this.getDefaultPeriod();
        this.startDate = startDate;
        this.endDate = endDate;
    };

    @action
    setStartDate = (date: string) => {
        const { data: dataStore } = this.stores;
        this.startDate = date;
        this.showSelectedPeriod = false;
        dataStore.logDataLayer('chart_period_selection', {
            chart: this.chartConfig.label,
            dateTarget: 'start',
            date,
            relatedEntityId: this.shareClass.clientCode,
        });
    };

    @action
    setEndDate = (date: string) => {
        const { data: dataStore } = this.stores;
        this.endDate = date;
        this.showSelectedPeriod = false;
        dataStore.logDataLayer('chart_period_selection', {
            chart: this.chartConfig.label,
            dateTarget: 'end',
            date,
            relatedEntityId: this.shareClass.clientCode,
        });
    };

    @action
    resetAutocompleteSelectedDates = () => {
        this.startAutoCompleteRef.current.setSelectedValue('');
        this.endAutoCompleteRef.current.setSelectedValue('');
    };

    @action
    disclaimerByType(disclaimerTypes) {
        const { queryContext } = this;
        const { data: dataStore } = this.stores;
        const { disclaimers = {} } = this.shareClass;
        return getDisclaimers(
            dataStore,
            disclaimerTypes,
            disclaimers,
            queryContext
        );
    }

    seriesHasData(key: string) {
        const { data } = this;
        if (!isNullOrEmpty(data)) {
            if (Array.isArray(data)) {
                return (
                    data.filter(item => !isNullOrEmpty(item[key])).length > 0
                );
            }
            return !isNullOrEmpty(data[key]);
        }
        return false;
    }

    getPeriodStartEndDates = (periodOption: PeriodSelectorOptions) => {
        const { period, value } = periodOption;
        const startDate = get(this.dataSet[0], this.dateProperty);
        const endDateString = get(
            this.dataSet[this.dataSet.length - 1],
            this.dateProperty
        );

        const endDate = new Date(endDateString);

        if (period === 'YTD') {
            // build the start date to last day of previous year to show data change for 1 Jan
            const ytdStartDate = `${new Date(endDate).getFullYear() - 1}-12-31`;
            // if no date on last day of previous year, filter dates to get date prior to ytdStartDate
            const fallbackDate = this.dataSet.filter(
                data => data[this.dateProperty] <= ytdStartDate
            );
            let actualStartDate = ytdStartDate;
            if (fallbackDate?.length > 0) {
                // take last date of dataset for fallback
                actualStartDate = fallbackDate[fallbackDate.length - 1].date;
            }
            return {
                startDate: actualStartDate,
                endDate,
                hasDataForPeriod: true,
            };
        }

        const periodDuration: number = value;
        const datePeriod: DatePeriod = period;
        const result = this.getPeriodStartAndEndDatesWithDatePeriods(
            periodDuration,
            endDate,
            datePeriod
        );
        if (result !== undefined) {
            return result;
        }

        return {
            startDate,
            endDate: endDateString,
            hasDataForPeriod: true,
        };
    };

    getPeriodStartAndEndDatesWithDatePeriods(
        periodDuration: number,
        dataEndDate: Date,
        datePeriod: DatePeriod
    ) {
        if (periodDuration) {
            // used to determine if there is more data older than the start date which would indicate that the current period is "full" and enabled
            let hasOlderData = false;

            // Calculate the start date using the config
            let workingStartDate = addDate(dataEndDate, {
                [datePeriod.toLowerCase()]: -periodDuration,
            });
            // Getting the date before, to allow the data to be use if the dataset date matches the start date
            // since we are doing a check if the data is after the start date, date fns isEqual did not work at the time of writing
            workingStartDate = addDate(workingStartDate, {
                days: -1,
            });
            const dataSet = this.dataSet.filter(data => {
                const currentDate = get(data, this.dateProperty);
                const isAfterCurrent = isAfter(currentDate, workingStartDate);
                // If there is data before the start date, the current period is enabled
                if (!isAfterCurrent) {
                    hasOlderData = true;
                }
                return isAfterCurrent;
            });

            // use the data start and end for representing the the actual start and ends
            const startDate = get(dataSet[0], this.dateProperty);
            dataEndDate = get(dataSet[dataSet.length - 1], this.dateProperty);

            return {
                startDate,
                endDate: dataEndDate,
                hasDataForPeriod: hasOlderData,
            };
        }
        return;
    }

    public getLabelName = label => {
        const { translation: translationStore } = this.stores;
        if (isString(label)) {
            return {
                value: translate(translationStore, label),
            };
        }

        const labelValue = get(label, 'value', label);
        const translatedLabelValue = translate(translationStore, labelValue);

        return {
            ...label,
            value: translatedLabelValue,
        };
    };

    public getSeriesName = (series: Series) => {
        const { queryContext } = this;
        const { translation: translationStore } = this.stores;
        let seriesName: string | undefined;

        if (series.nameQuery) {
            const data = getQueryResults(queryContext, {
                name: series.nameQuery,
            });
            if (data && data.name) {
                seriesName = data.name;
            }
        }

        if (!seriesName && series.nameProperty) {
            seriesName = get(
                this.shareClass.properties_pub,
                `${series.nameProperty}.value`,
                series.name
            );
            const { namePropertyIndex } = series;
            if (
                namePropertyIndex !== undefined &&
                isArrayLike(seriesName) &&
                seriesName.length > namePropertyIndex
            ) {
                seriesName = seriesName[namePropertyIndex];
            } else if (isArrayLike(seriesName)) {
                seriesName = `${series.nameProperty} ${namePropertyIndex + 1}`;
            }
        }

        if (!seriesName) {
            seriesName = series.name;
        }

        const translatedSeriesName = translate(translationStore, seriesName);
        return translatedSeriesName;
    };

    getBaseFormatOptions = (formatOptions: any, v?: any) => {
        const { fundCurrency, locale } = this;
        const { data: dataStore } = this.stores;
        const base = {
            dataStore,
            locale,
            format: { ...formatOptions },
            type: formatOptions.type,
            currency: fundCurrency,
            options: { ...formatOptions.options },
            value: undefined,
        };
        return !isNullOrUndefined(v) ? { ...base, value: v } : base;
    };
}



// WEBPACK FOOTER //
// ./src/stores/ChartStore.ts